<template>
    <div class="el-date-editor el-input el-input--suffix" :class="editorClasses">
        <div ref="triggerRef" class="el-date-editor__trigger">
            <ElInput
                ref="inputRef"
                :placeholder="placeholder"
                :model-value="displayValue"
                :label="label"
                readonly
                suffix-icon="i-calendar--16"
                :clearable="clearable"
                :on-icon-click="togglePanel"
                @click="openPanel"
                @input="onInput"
                @keydown="onKeydown"
                @clear="onClear"
            />
        </div>

        <transition :name="transition" @after-leave="onClosed">
            <div
                v-show="visible"
                ref="dropdownRef"
                v-clickoutside="{ handler: onClosed, include: [triggerRef, dropdownRef] }"
                class="el-picker-panel"
                :class="panelClasses"
            >
                <div v-if="isReady" class="el-picker-panel__body-wrapper">
                    <div class="el-picker-panel__body">
                        <DateRangePanel
                            v-if="isRange"
                            :key="'range-' + panelKey"
                            v-model:start="rangeState.start"
                            v-model:end="rangeState.end"
                            :first-day-of-week="firstDayOfWeek"
                            :unlink-panels="unlinkPanels"
                            @pick="onPickRange"
                        />

                        <DatePanel
                            v-else
                            :key="'single-' + panelKey"
                            v-model="innerDate"
                            :first-day-of-week="firstDayOfWeek"
                            @pick="onPickSingle"
                        />
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useFirstDayOfWeek } from '@core/components/date-picker/mixins/use-first-day-of-week';
import DatePanel from '@core/components/date-picker/panel/DatePanel.vue';
import DateRangePanel from '@core/components/date-picker/panel/DateRangePanel.vue';
import { formatDate, parseDate } from '@core/components/date-picker/util/index';
import ElInput from '@core/components/input/input.vue';
import { usePopper } from '@core/composables/usePopper';
import Clickoutside from '@core/directives/v-clickoutside';
// import '@core/styles/date-picker.scss';

defineOptions({
    directives: { clickoutside: Clickoutside },
});

const props = withDefaults(
    defineProps<{
    modelValue?: Date | [Date, Date] | null;
    type?: 'date' | 'daterange';
    placeholder?: string;
    label?: string;
    placement?: string;
    popperClass?: string;
    format?: string;
    transition?: string;
    clearable?: boolean;
    unlinkPanels?: boolean;
    isFormatForPayload?: boolean;
  }>(),
    {
        type: 'date',
        placement: 'bottom-start',
        modelValue: null,
        placeholder: '',
        transition: '',
        label: '',
        popperClass: '',
        format: 'yyyy-MM-dd',
        clearable: true,
        unlinkPanels: false,
        isFormatForPayload: false,
    },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: any): void;
  (e: 'change', v: any): void;
}>();

const { firstDayOfWeek } = useFirstDayOfWeek(1);
const visible = ref(false);
const isReady = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const triggerRef = ref<HTMLInputElement | null>(null);
const dropdownRef = ref<HTMLInputElement | null>(null);

const panelKey = ref(0);
const isRange = computed(() => props.type === 'daterange');

const innerDate = ref<Date | null>(
    Array.isArray(props.modelValue) ? null : (props.modelValue as Date | null) || null,
);
const rangeState = ref<{ start: Date | null; end: Date | null }>({
    start: Array.isArray(props.modelValue) ? props.modelValue?.[0] || null : null,
    end: Array.isArray(props.modelValue) ? props.modelValue?.[1] || null : null,
});

watch(
    () => props.modelValue,
    (v) => {
        if (Array.isArray(v)) {
            rangeState.value = { start: v?.[0] || null, end: v?.[1] || null };
        } else {
            innerDate.value = (v as Date) || null;
        }
        if (!v) {
            onClear();
        }
    },
);

const displayValue = computed(() => {
    if (isRange.value) {
        const s = rangeState.value.start
            ? formatDate(rangeState.value.start, props.format)
            : '';
        const e = rangeState.value.end ? formatDate(rangeState.value.end, props.format) : '';
        return s && e ? `${s} - ${e}` : '';
    }
    return innerDate.value ? formatDate(innerDate.value, props.format) : '';
});

const editorClasses = computed(() => ({
    'el-date-editor--daterange': isRange.value,
}));

const panelClasses = computed(() => ({
    'el-date-range-picker': isRange.value,
    'el-date-picker': !isRange.value,
}));

const popperOptions = computed(() => ({
    placement: props.placement,
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { boundary: 'viewport' } },
        { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
    ],
}));

const { initializePopper, updatePopper, destroyPopper } = usePopper(
    triggerRef,
    dropdownRef,
    popperOptions.value,
);

watch(visible, (val) => {
    if (val) {
        nextTick(async () => {
            await initializePopper();
            requestAnimationFrame(() => updatePopper());
            isReady.value = true;
        });
    } else {
        destroyPopper();
        isReady.value = false;
    }
});

function onClear() {
    innerDate.value = null;
    rangeState.value = { start: null, end: null };

    emit('update:modelValue', null);
    emit('change', null);

    panelKey.value++;
}

function openPanel(e: MouseEvent) {
    e.stopPropagation();
    visible.value = true;
}
function togglePanel() {
    visible.value = !visible.value;
}
function onClosed() {
    visible.value = false;
    destroyPopper();
}

function onPickSingle(d: Date) {
    innerDate.value = d;
    const value = props.isFormatForPayload ? formatDate(d, props.format) : d;
    emit('update:modelValue', value);
    emit('change', value);
    visible.value = false;
}

function onPickRange([start, end]: [Date, Date]) {
    rangeState.value = { start, end };
    emit('update:modelValue', [start, end]);
    emit('change', [start, end]);
    visible.value = false;
}

function onInput(e: Event) {
    const v = (e.target as HTMLInputElement)?.value;
    if (!v || isRange.value) return;
    const d = parseDate(v, props.format);
    if (d && d instanceof Date && !Number.isNaN(d.getTime())) onPickSingle(d);
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') visible.value = false;
    if (e.key === 'Tab') visible.value = false;
}

onMounted(() => {
    nextTick(() => visible.value && initializePopper());
    window.addEventListener('resize', updatePopper);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', updatePopper);
    destroyPopper();
});
</script>

<style lang="scss">
/* TODO: must be scoped */

/* Panel shell */
.el-picker-panel {
  position: absolute;
  color: var(--av-fixed-primary);
  border: 1px solid var(--av-brand-primary);
  box-shadow: var(--el-shadow-regular);
  background-color: var(--av-inversed-primary);
  border-radius: 4px;
  z-index: 1030;
  margin: 5px 0;
}

.el-picker-panel__body-wrapper {
  display: inline-block;
}

.el-picker-panel__body-wrapper::after,
.el-picker-panel__body::after {
  content: "";
  display: table;
  clear: both;
}

.el-picker-panel__body {
  position: relative;
}

.el-picker-panel__content {
  position: relative;
}

.el-date-picker {
  display: inline-block;
}

.el-picker-panel__content--multi {
  border-bottom: 1px solid var(--av-brand-lighter);
}

.el-picker-panel__footer {
  border-top: 1px solid var(--av-brand-lighter);
  padding: 4px;
  text-align: right;
  background-color: var(--av-inversed-primary);
  position: relative;
  font-size: 0;
}

.el-picker-panel__footer-multi {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
}

.el-picker-panel__footer-multi.is-one-day {
  justify-content: flex-end;
}

.el-picker-panel__footer-multi--clear-btn {
  max-width: 158px;
  margin-left: 4px;
  font-weight: 400 !important;
}

.el-picker-panel__footer-multi--all-btn {
  max-width: 90px;
  margin-left: 16px;
}

/* Sidebar / predefined */
.el-picker-panel__sidebar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 160px;
  border-right: 1px solid var(--av-brand-lighter);
  box-sizing: border-box;
  padding: 12px 16px;
  background-color: var(--av-inversed-primary);
  overflow: auto;
}

.el-picker-panel__shortcut {
  display: block;
  width: 100%;
  border: none;
  background-color: transparent;
  line-height: 28px;
  font-size: 14px;
  color: var(--av-fixed-primary);
  padding-left: 12px;
  text-align: left;
  outline: none;
  cursor: pointer;
}

.el-picker-panel__shortcut.active,
.el-picker-panel__shortcut:hover {
  background-color: var(--av-brand-accent);
}

.el-picker-panel__btn {
  border: 1px solid var(--av-fixed-light);
  color: var(--av-fixed-light);
  line-height: 24px;
  border-radius: 2px;
  padding: 0 20px;
  cursor: pointer;
  background-color: transparent;
  outline: none;
  font-size: 12px;
}

.el-picker-panel__btn[disabled] {
  color: var(--av-fixed-lighter);
  cursor: default;
}

.el-picker-panel__icon-btn {
  font-size: 12px;
  color: var(--av-fixed-primary);
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
  margin-top: 8px;
}
.el-picker-panel__icon-btn:hover {
  color: var(--av-brand-primary);
}
.el-picker-panel__icon-btn.is-disabled {
  color: var(--av-fixed-light);
}
.el-picker-panel__icon-btn.is-disabled:hover {
  cursor: default;
}

/* Date picker header */
.el-date-picker__header,
.el-date-range-picker__header {
  height: 48px;
  padding: 0 16px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--av-brand-lighter);
  max-width: 296px;
}

.el-date-picker__header--multi {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.el-date-picker__label-wrapper {
  height: 100%;
  display: inline-block;
}
.el-date-picker__header > span,
.el-date-range-picker__header > span {
  flex: 1;
  text-align: center;
}

.el-date-picker__header-btn {
  color: var(--av-brand-primary);
  cursor: pointer;
}

.el-date-picker__header-btn--with-spacer-left {
  margin-left: 8px;
}
.el-date-picker__header-btn--with-spacer-right {
  margin-right: 8px;
}
.el-date-picker__header-btn.disabled {
  cursor: default;
}

.el-date-picker__header-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  font-size: 18px;
  font-weight: 400;
  padding: 0 8px;
  line-height: 48px;
  text-align: center;
  cursor: pointer;
  color: var(--av-fixed-primary);
}
.el-date-picker__header-label:hover {
  background-color: var(--av-brand-accent);
}
.el-date-picker__header-label.disabled {
  cursor: default;
}
.el-date-picker__header-label.disabled.active,
.el-date-picker__header-label.disabled:hover {
  background-color: inherit;
}

.el-date-picker__header-label-month {
  max-width: 68px;
}
.el-date-picker__header-label-year {
  max-width: 100px;
}
.el-date-picker__header-label-year--full-width {
  max-width: 200px;
}

/* Date table (days grid) */
.el-date-table {
  font-size: 14px;
  font-weight: 400;
  user-select: none;
  margin: 0;
  table-layout: fixed;
  line-height: 32px;
  width: auto;
}
.el-date-table__row {
  font-size: 14px;
}
.el-date-table__row--hidden {
  display: none;
}
.el-date-table__weekday-name {
  max-width: 40px;
  text-align: center;
}

.el-date-table tbody::before,
.el-date-table thead::before {
  content: "";
  display: block;
  height: 8px;
}

.el-date-table thead th {
  font-size: 14px;
  font-weight: 400;
  line-height: 32px;
  width: 40px;
  text-align: center;
  height: 32px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--av-brand-lighter);
  padding: 0;
}
.el-date-table tbody::after,
.el-date-table thead th::after {
  content: "";
  display: block;
  height: 8px;
}

.el-date-table tbody td {
  font-size: 14px;
  font-weight: 400;
  width: 40px;
  text-align: center;
  height: 32px;
  cursor: pointer;
  position: relative;
  border: none;
  padding: 0;
}
.el-date-table tbody td div {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
.el-date-table tbody td span {
  width: 40px;
  height: 32px;
  display: block;
  text-align: center;
  line-height: 32px;
}

/* Day states */
.el-date-table tbody td.available:hover div,
.el-date-table tbody td.in-range div,
.el-date-table tbody td.in-range div:hover {
  background-color: var(--av-brand-accent);
}

.el-date-table tbody td.start-date div,
.el-date-table tbody td.start-date div:hover,
.el-date-table tbody td.end-date div,
.el-date-table tbody td.end-date div:hover {
  background-color: var(--av-brand-light);
}

.el-date-table tbody td.current div,
.el-date-table tbody td.current div:hover {
  background-color: var(--av-brand-accent);
}

.el-date-table tbody td.today span {
  font-weight: 600;
}

.el-date-table tbody td.disabled div {
  opacity: 1;
  cursor: default;
  color: var(--av-fixed-lightest);
}

.el-date-table tbody td.prev-month div,
.el-date-table tbody td.next-month div {
  color: var(--av-inversed-primary);
  cursor: default;
}

.el-date-table tbody td.prev-month.current div,
.el-date-table tbody td.prev-month.disabled div,
.el-date-table tbody td.prev-month.in-range div,
.el-date-table tbody td.prev-month.start-date div,
.el-date-table tbody td.prev-month.end-date div,
.el-date-table tbody td.prev-month.start-date-consecutive div,
.el-date-table tbody td.next-month.current div,
.el-date-table tbody td.next-month.disabled div,
.el-date-table tbody td.next-month.in-range div,
.el-date-table tbody td.next-month.start-date div,
.el-date-table tbody td.next-month.end-date div,
.el-date-table tbody td.next-month.start-date-consecutive div {
  background-color: var(--av-inversed-primary);
  color: var(--av-inversed-primary);
  cursor: default;
}
.el-date-table tbody td.prev-month:hover div,
.el-date-table tbody td.next-month:hover div {
  background-color: var(--av-inversed-primary);
}

.el-date-table .weekends {
  color: var(--av-fixed-light);
}
.el-date-table.is-multi-mode .weekends {
  color: var(--av-fixed-primary);
}

/* Month table */
.el-month-table {
  margin: 32px 8px;
  border-collapse: collapse;
}
.el-month-table td {
  text-align: center;
  padding: 16px 0;
  border: none;
}
.el-month-table td .cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 70px;
  height: 32px;
  display: block;
  line-height: 32px;
  margin: 0 auto;
  color: var(--av-fixed-primary);
  cursor: pointer;
}
.el-month-table td .cell:hover {
  color: var(--av-fixed-primary);
}
.el-month-table td .cell:hover,
.el-month-table td.current:not(.disabled) .cell {
  background-color: var(--av-brand-accent);
}
.el-month-table td.disabled .cell {
  cursor: default;
}
.el-month-table td.disabled .cell,
.el-month-table td.disabled .cell:hover {
  background-color: var(--av-inversed-primary);
  color: var(--av-fixed-lightest);
}

/* Year table */
.el-year-table {
  margin: 32px 8px;
  border-collapse: collapse;
}
.el-year-table td {
  text-align: center;
  padding: 16px 0;
  border: none;
}
.el-year-table td .cell {
  width: 70px;
  height: 32px;
  display: block;
  line-height: 32px;
  margin: 0 auto;
  color: var(--av-fixed-primary);
  cursor: pointer;
}
.el-year-table td .cell:hover {
  color: var(--av-fixed-primary);
}
.el-year-table td .cell:hover,
.el-year-table td.current:not(.disabled) .cell {
  background-color: var(--av-brand-accent);
}
.el-year-table td.disabled .cell {
  cursor: default;
}
.el-year-table td.disabled .cell,
.el-year-table td.disabled .cell:hover {
  background-color: var(--av-inversed-primary);
  color: var(--av-fixed-lightest);
}

/* Date editor / input */
.el-date-editor {
  position: relative;
  text-align: left;
}
.el-date-editor .el-input__suffix i,
.el-date-editor.is-non-editable .el-input__container,
.el-date-editor.is-non-editable .el-input__container input {
  cursor: pointer;
}
.el-date-editor.is-non-editable.is-disabled .el-input__container,
.el-date-editor.is-non-editable.is-disabled .el-input__container input {
  cursor: default;
}

/* Range picker layout */
.el-date-range-picker {
  display: inline-flex;
}
.el-date-range-picker .el-picker-panel__body {
  display: inline-block;
  flex-flow: row wrap;
  width: auto;
}
@media (--viewport-tablet) {
  .el-date-range-picker .el-picker-panel__body {
    display: flex;
    flex-flow: row nowrap;
  }
}
.el-date-range-picker__content {
  box-sizing: border-box;
  margin: 0;
}
.el-date-range-picker__content.is-right .el-date-range-picker__header div {
  margin-left: 50px;
  margin-right: 50px;
}
.el-date-range-picker__content:not(:first-child) {
  border-left: 1px solid var(--av-brand-accent);
}

.el-date-range-picker__predefined {
  border-left: 1px solid var(--av-brand-accent);
  width: 160px;
  padding: 12px 16px;
}
.el-date-range-picker__predefined-setting {
  display: inline-block;
}

.el-date-range-picker__action-panel {
  border-top: 1px solid var(--av-brand-accent);
  height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
}

/* Custom options checklist */
.el-custom-options {
  position: relative;
  border-bottom: 1px solid var(--av-brand-lighter);
  width: 296px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.el-custom-options .el-checkbox-group__content {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
}
.el-custom-options__item {
  flex: 0 1 auto;
}

/* Utility width for first/last weekday columns */
.el-date-table tbody td:first-child,
.el-date-table thead th:first-child {
  width: 48px;
  padding-left: 8px;
}
.el-date-table tbody td:last-child,
.el-date-table thead th:last-child {
  width: 48px;
  padding-right: 8px;
}
</style>
